import { FileText, X } from "lucide-react";
import { formatMoney, useCanEdit } from "@/lib/app-context";
import { fmtDate, balanceLabel, curOf } from "@/lib/sheet-utils";
import { MoneyInput } from "@/components/MoneyInput";
import { PaidToggle } from "@/components/PaidToggle";
import { NoteInput } from "@/components/NoteInput";
import { BonusTypeSelect } from "@/components/BonusTypeSelect";

const Cell = ({ label, children }) => (
  <div className="border border-slate-200 rounded-lg px-1 pt-1 min-w-0"><div className="text-[10px] uppercase font-bold text-slate-500 px-2">{label}</div>{children}</div>
);
const Info = ({ label, value, testId }) => (
  <div className="bg-slate-50 rounded-lg px-3 py-1.5 min-w-0"><div className="text-[10px] uppercase font-bold text-slate-500">{label}</div><div className="font-mono text-sm font-semibold truncate" data-testid={testId}>{value}</div></div>
);
const Row = ({ label, value, className = "" }) => (
  <div className={`flex justify-between gap-2 ${className}`}><span>{label}</span><span>{value}</span></div>
);

function Card({ r, onPatch, onSlip, onRemove }) {
  const canEdit = useCanEdit();
  const m = (n) => formatMoney(n, curOf(r));
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4" data-testid={`salary-card-${r.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-heading font-bold text-lg truncate">{r.staff_name}</div>
          <div className="text-xs text-slate-500"><span className="font-bold text-slate-700" data-testid={`m-currency-${r.id}`}>{curOf(r)}</span> · Agent: {r.agent_name || "—"} · Joined {fmtDate(r.joining_date) || "—"}</div>
        </div>
        <PaidToggle row={r} onToggle={() => onPatch(r, { paid: !r.paid })} testId={`m-paid-toggle-${r.id}`} />
      </div>
      <div className="grid grid-cols-2 gap-2 mt-3">
        <Cell label="Salary"><MoneyInput value={r.salary} onCommit={(v) => onPatch(r, { salary: v })} testId={`m-input-salary-${r.id}`} /></Cell>
        <Cell label="Days /30"><MoneyInput value={r.days} onCommit={(v) => onPatch(r, { days: Math.min(v, 30) })} testId={`m-input-days-${r.id}`} /></Cell>
        <Info label="Earned" value={m(r.earned)} testId={`m-earned-${r.id}`} />
        <Info label="B/F balance" value={m(r.brought_forward)} testId={`m-bf-${r.id}`} />
        <Cell label="Bonus"><MoneyInput value={r.bonus || 0} onCommit={(v) => onPatch(r, { bonus: v })} testId={`m-input-bonus-${r.id}`} className="text-emerald-700" /><BonusTypeSelect value={r.bonus_type} onChange={(v) => onPatch(r, { bonus_type: v })} testId={`m-bonus-type-${r.id}`} /></Cell>
        <Cell label="Advance"><MoneyInput value={r.advance} onCommit={(v) => onPatch(r, { advance: v })} testId={`m-input-advance-${r.id}`} /></Cell>
        <Cell label="Deduct"><MoneyInput value={r.deduct} onCommit={(v) => onPatch(r, { deduct: v })} testId={`m-input-deduct-${r.id}`} /></Cell>
        <Info label="Payable" value={m(r.payable)} testId={`m-payable-${r.id}`} />
        <Cell label="Paid amt"><MoneyInput value={r.paid_amount} onCommit={(v) => onPatch(r, { paid_amount: v })} testId={`m-input-paid-${r.id}`} /></Cell>
      </div>
      <NoteInput value={r.note} onCommit={(v) => onPatch(r, { note: v })} testId={`m-input-note-${r.id}`} className="mt-2 border border-slate-200 rounded-lg px-2" />
      <div className="flex items-center justify-between mt-3">
        <div>
          <div className="text-[10px] uppercase font-bold text-slate-500">Balance c/f · {balanceLabel(r.balance)}</div>
          <div className={`font-mono text-xl font-extrabold ${r.balance < 0 ? "text-red-600" : ""}`} data-testid={`m-balance-${r.id}`}>{m(r.balance)}</div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onSlip(r)} className="p-2 rounded-lg bg-teal-50 text-teal-700" data-testid={`m-payslip-btn-${r.id}`}><FileText size={18} /></button>
          {canEdit && <button onClick={() => onRemove(r)} className="p-2 rounded-lg bg-slate-100 text-slate-500" data-testid={`m-remove-entry-${r.id}`}><X size={18} /></button>}
        </div>
      </div>
    </div>
  );
}

export function SalaryCards({ rows, groups, ...handlers }) {
  return (
    <div className="md:hidden space-y-3" data-testid="salary-cards">
      {rows.map((r) => <Card key={r.id} r={r} {...handlers} />)}
      {groups.map(({ code, count, t: totals, combined }) => { const m = (n) => formatMoney(n, code); const id = combined ? "combined" : code; return (
      <div key={id} className={`${combined ? "bg-teal-900" : "bg-[var(--ink)]"} text-white rounded-xl p-4 font-mono text-sm space-y-1`} data-testid={`m-totals-${id}`}>
        <div className="font-heading font-bold text-base mb-2">{combined ? `Combined in ${code}` : `Total ${code}`} · {count} staff</div>
        <Row label="Earned" value={m(totals.earned)} />
        <Row label="B/F" value={m(totals.brought_forward)} />
        <Row label="Bonus" value={m(totals.bonus)} />
        <Row label="Advance + deduct" value={m(totals.advance + totals.deduct)} />
        <Row label="Payable" value={<span data-testid={`m-total-payable-${id}`}>{m(totals.payable)}</span>} className="text-teal-300 font-bold" />
        <Row label="Paid" value={m(totals.paid_amount)} />
        <Row label="Balance c/f" value={<span data-testid={`m-total-balance-${id}`}>{m(totals.balance)}</span>} className="text-amber-300 font-bold text-base pt-1 border-t border-white/20" />
      </div>
      ); })}
    </div>
  );
}
